import { getFlareAttr } from '@common/flare/prosemirror/transform/node';
import { FoundNodeInfo } from '@common/prosemirror/model/node-find';
import { cloneDeep } from 'lodash';
import { Node, NodeSpec } from 'prosemirror-model';

/**
 * Checks if a node is a bookmark node.
 * A bookmark node is either a link with a name attribute and no href attribute or the legacy madcapbookmark node.
 * @param node The ProseMirror node to check.
 * @returns Returns true if the node is a bookmark node, false otherwise.
 */
export function isBookmarkNode(node: Node): boolean {
  return node.type.name === 'madcapbookmark' || (node.type.name === 'link' && getFlareAttr(node, 'name') && !getFlareAttr(node, 'href'));
}

function textBlockExists(parent: Node): boolean {
  for (let i = 0; i < parent.childCount; i++) {
    if (parent.child(i).textContent) {
      return true;
    }
  }
  return false;
}

// a bucket is a grouping of the same nodes
function isBucket(node: Node, parent: Node, pos: number): boolean {
  // if a bookmark we dont care if it has textContent, if its not has to have textContent and a linkBucket
  // or needs to be a body tag because that's how to get the Top of The Document later on
  return (!node.textContent && isBookmarkNode(node)) || (!!node.textContent && textBlockExists(node) && (node.type.name === 'list_item' || !!node.type.spec.linkBucket) || node.type.name === 'body');
}

function getBucketName(doc: Node, node: Node, parent: Node, pos: number): NodeSpec['linkBucket'] | NodeSpec['childLinkBucket'] | 'body' {
  const currentParentBucket = parent.type.spec.linkBucket;
  const currentParentChildBucket = parent.type.spec.childLinkBucket;
  const currentNodeBucket = node.type.spec.linkBucket;

  // grab the body tag to get the Top Of The Document Node later
  if (node.type.name === 'body') {
    return 'body';
  }

  // p elements under any of li, ul, ol, td, dropdown, or expanding text belong in those sections
  if (currentNodeBucket === 'paragraph') {
    // paragraph acting normally cases
    if (currentParentBucket === 'heading' || currentParentBucket === 'bookmark' || currentParentBucket === 'micro_content') {
      return currentNodeBucket;
    }
    //list item, grab the grandparent
    else if (parent.type.name === 'list_item') {
      const resolvedPos = doc.resolve(pos);
      const grandparent = resolvedPos.node(resolvedPos.depth - 1);
      return grandparent.type.spec.childLinkBucket;
    }
    else if (currentParentBucket) {
      return currentParentBucket;
    }
  }

  return currentParentChildBucket ?? currentNodeBucket;
}

export function getNodeBuckets(doc: Node): Dictionary<FoundNodeInfo[]> {
  const buckets: Dictionary<FoundNodeInfo[]> = {};
  // current nodes to exclude just for styles
  const excludedFromStyles: string[] = ['body', "table", "table_row", "madcapfootnote", "madcapfootnoteblock"];

  // Loop through the node's descendants
  doc.descendants((child: Node, pos: number, parent: Node) => {
    const data = { node: child, parent: parent, pos: pos };
    if (isBucket(child, parent, pos)) {
      // fill up bucket or create it
      const bucketName = getBucketName(doc, child, parent, pos);
      (buckets[bucketName] ??= []).push(data);
    }

    //styles bucket --- dont shallow copy this node, because we want the name attribute to not change if the node is already in one of the buckets.
    const stylesExists = getFlareAttr(child, 'class') ?? getFlareAttr(child, 'id');
    if (stylesExists && !excludedFromStyles.includes(child.type.name) && textBlockExists(child)) {
      (buckets['styles'] ??= []).push(cloneDeep(data));
    }

  });

  return buckets;
}
